/**
 * interior_capability.ts — WHAT MAY OPEN, ON PURPOSE (the interiors programme, slice A, 2026-10-01). Pure: no imports.
 *
 * Until now a building opened because of how its key was SPELLED: an exclusion regex, an inclusion regex and, for the
 * opaque civ-pack keys, an allow-set derived from the program tables (`building_interiors.ts`). That left accidents
 * nobody chose — `mining_facility` opens because "mining" does not contain "mine"; the sky-ship hull would open because
 * "city_hall" contains "hall"; a future `warehouse` would open because it contains "house".
 *
 * This table names every identity the game can place TODAY and gives it one of four classes and a reason:
 *
 *   habitable      a room people live, meet or are served in — opens when it is big enough for a shell
 *   staffSupport   an industrial or service structure whose ROOM is the staff side — opens the same way
 *   equipmentOnly  a machine, a prop, a fixture, a piece of decor — never a room
 *   sealed         a building that stays shut by policy (the civ packs' shops, warehouses and factories; walls)
 *
 * THE ADMISSION LAW OF THIS SLICE (the owner's decision): admission does NOT change. Every `capability` below is what
 * the old predicate answered for that key — `verify:interior-coverage` runs the frozen pre-table predicates beside the
 * table over every identity, every baked Commons placement and a fuzz, and fails on one difference. Where the audit
 * thinks today's answer is an accident, the entry carries `recommend` and `question` instead: the owner's list, never a
 * silent change. To accept one, copy `recommend` over `capability` — the gate's snapshot then shows a deliberate diff.
 *
 * The eligibility functions read this table FIRST; the regexes remain only for keys it does not name (the legacy
 * human-readable prefab names: `hab_pod_medium`, `council_chamber`, …). A NEW structure type or civ key must be added
 * here on purpose — the gate fails on an unclassified identity.
 */

export type InteriorCapability = 'habitable' | 'staffSupport' | 'equipmentOnly' | 'sealed';

export interface CapabilityEntry {
  /** TODAY's class — the one admission reads. */
  capability: InteriorCapability;
  /** Why it has that class, in one line a person can read. */
  reason: string;
  /** The audit's class, when it differs from today's (the owner's list — never applied by code). */
  recommend?: InteriorCapability;
  /** The question for the owner. */
  question?: string;
}

/** The classes whose buildings open (subject to their namespace's size floor). */
export function admits(c: InteriorCapability): boolean {
  return c === 'habitable' || c === 'staffSupport';
}

const hab = (reason: string, q?: Pick<CapabilityEntry, 'recommend' | 'question'>): CapabilityEntry => ({ capability: 'habitable', reason, ...q });
const staff = (reason: string, q?: Pick<CapabilityEntry, 'recommend' | 'question'>): CapabilityEntry => ({ capability: 'staffSupport', reason, ...q });
const equip = (reason: string): CapabilityEntry => ({ capability: 'equipmentOnly', reason });
const shut = (reason: string, q?: Pick<CapabilityEntry, 'recommend' | 'question'>): CapabilityEntry => ({ capability: 'sealed', reason, ...q });

/** A lookup table with NO prototype: a key called `constructor` or `toString` is simply absent. */
function table(rows: Record<string, CapabilityEntry>): Readonly<Record<string, CapabilityEntry>> {
  const t: Record<string, CapabilityEntry> = Object.create(null);
  for (const k of Object.keys(rows)) t[k] = Object.freeze(rows[k]);
  return Object.freeze(t);
}

/** No exclusion word names it, so today the size floor alone decides — the wording the audit entries share. */
const BY_SIZE = 'no exclusion word names it — the size floor alone decides';

// ── Deeds: every type in the server's STRUCTURE_CATALOG (37) ─────────────────────────────────────────────────────────
export const DEED_CAPABILITY: Readonly<Record<string, CapabilityEntry>> = table({
  // homes
  small_house: hab('a home'),
  medium_house: hab('a home'),
  large_house: hab('a home'),
  xlarge_house: hab('a home (the Manor)'),
  hotel: hab('lodging'),
  // halls and services
  guild_hall: hab('the guild\'s great hall (its own shell and furnishing)'),
  city_hall: hab('the civic seat'),
  cantina: hab('a public house'),
  hospital: hab('a clinic'),
  bank: hab('a banking hall'),
  garage: hab('a service bay with a front desk'),
  theater: hab('an auditorium'),
  cloning: hab('a clinic'),
  shuttleport: hab('a passenger hall'),
  spaceport: hab('a terminal building'),
  museum: hab('a gallery'),
  store: hab('a shop'),
  market: hab('a covered market'),
  salon: hab('a personal-service shop'),
  bunker: hab('a shelter'),
  outpost: hab('a watch post'),
  // industry the rule admits
  factory: staff('an industrial deed: the room is the staff side (the workshop plan), the machinery stays the exterior\'s', {
    question: 'a factory DEED opens today while both civ packs\' factory keys are sealed — confirm the deed stays a staff room',
  }),
  mining_facility: staff('an industrial deed: the exclusion word is "mine" and "mining" does not contain it', {
    question: 'a mining-facility DEED opens today (workshop plan) by a spelling accident, while the civ packs\' mining_facility keys are sealed — keep it as staff support, or seal it',
  }),
  // DECIDED 2026-10-01 (the owner: "ok fix it to your recommendations"): the harvester is a MACHINE — a four-legged
  // field rig with a hopper, a conveyor and an operator's cabinet, its own model since the same day (6.9 × 7.4 × 6.5 m,
  // past every size floor). It is equipment by CLASS now, so no catalog scale can ever open it as a room (it was shut
  // only because the old model was kept 5.65 m wide). interiors.md always said a harvester never opens; now the table does.
  harvester: equip('a field machine (legs, a hopper, a drill column) — there is no room in it at any size'),
  // things the vocabulary never excluded and only their size keeps shut
  // DECIDED 2026-10-01 (the same answer): a tent is a STALL — an open canopy over a counter a person stands AT (its
  // own model since the same day, 4.2 × 2.9 × 3.8 m). Sealed by class: no size turns a stall into a walk-in room. A
  // market-stall archetype, if one is ever built, re-opens it here and nowhere else.
  merchant_tent: shut('a stall (an open canopy over a counter), not a room — you stand at it, never in it'),
  civic_decoration: hab(BY_SIZE, { recommend: 'equipmentOnly', question: 'a decoration is not a room (its model is an obelisk 2.4 m round since 2026-10-01 — refused by size alone)' }),
  garden: hab(BY_SIZE, { recommend: 'equipmentOnly', question: 'an open garden has no roof to carve a room under' }),
  pool: hab(BY_SIZE, { recommend: 'equipmentOnly', question: 'an open pool is an outdoor amenity — only its 3 m height keeps it shut' }),
  city_console: hab(BY_SIZE, { recommend: 'equipmentOnly', question: 'a console is a fixture' }),
  // machines, props and walls (the exclusion words)
  extractor_small: equip('a resource extractor (the word "extract")'),
  extractor_medium: equip('a resource extractor (the word "extract")'),
  extractor_large: equip('a resource extractor (the word "extract")'),
  turret: equip('a weapon emplacement (the word "turret")'),
  statue: equip('a monument (the word "statue")'),
  fountain: equip('a fountain (the word "fountain")'),
  street_lamp: equip('a lamp post (the word "lamp")'),
  city_wall: shut('a wall segment (the word "wall")'),
});

// ── Settlements: every key the settlement builder can place (47 civ-pack keys + 3 port keys) ────────────────────────
const A = (k: string): string => `structure:civa_${k}`;
const B = (k: string): string => `structure:civb_${k}`;
const HOUSE = 'house program', APART = 'apartment program', OFFICE = 'office program', SKY = 'skyscraper program';
const SHOP_OVERLAP = {
  question: 'also the SHOP program\'s pick: a pad placed for a shop opens as a tower, because the enterable keeps the key and not the function it was placed for',
};

export const SETTLEMENT_CAPABILITY: Readonly<Record<string, CapabilityEntry>> = table({
  // Civilization A — the Aurelian pack
  [A('scifi5')]: hab(HOUSE), [A('scifi6')]: hab(HOUSE), [A('scifi8')]: hab(HOUSE),
  [A('scifi10')]: hab(HOUSE), [A('scifi19')]: hab(HOUSE), [A('scifi20')]: hab(HOUSE),
  [A('scifi2')]: hab(APART), [A('scifi4')]: hab(APART), [A('scifi7')]: hab(APART), [A('scifi9')]: hab(APART), [A('scifi11')]: hab(APART),
  [A('scifi12')]: hab(OFFICE), [A('scifi13')]: hab(OFFICE), [A('scifi18')]: hab(OFFICE), [A('scifi1a')]: hab(OFFICE),
  [A('scifi3')]: hab(SKY), [A('scifi16')]: hab(SKY), [A('scifi17')]: hab(SKY),
  [A('scifi14')]: hab(`${SKY} (also the shop program and the commerce / concourse functions)`, SHOP_OVERLAP),
  [A('scifi15')]: hab(`${SKY} (also the shop program)`, SHOP_OVERLAP),
  [A('outpost')]: hab('command / civic function (also the defense, transit, docking, customs, airlock, barracks and armory pick)'),
  [A('survey_structure')]: hab('command / civic function (also science, agriculture and life support)', {
    question: 'civ A\'s survey structure opens as a civic hall while civ B\'s is sealed — only because A lists it under command / civic',
  }),
  [A('leasure_structure')]: shut('shop program; commerce, market, recreation, entertainment and concourse functions; a landmark'),
  [A('storage_structure')]: shut('warehouse program; storage, manufacturing, transit, landing and armory functions; a landmark'),
  [A('mining_facility')]: shut('factory program; industry, manufacturing, utility, repair, fuel and reactor functions'),
  // Civilization B — the Borean pack
  [B('city5')]: hab(HOUSE), [B('city8')]: hab(HOUSE), [B('city10')]: hab(HOUSE),
  [B('city13')]: hab(HOUSE), [B('city17')]: hab(HOUSE), [B('city18')]: hab(HOUSE),
  [B('city2')]: hab(APART), [B('city6')]: hab(APART), [B('city7')]: hab(APART), [B('city9')]: hab(APART),
  [B('city11')]: hab(OFFICE), [B('city12')]: hab(OFFICE), [B('city1a')]: hab(OFFICE),
  [B('city3')]: hab('office and skyscraper programs; civic function'),
  [B('city4')]: hab('skyscraper program; command and civic functions'),
  [B('city15')]: hab(SKY), [B('city16')]: hab(SKY),
  [B('city14')]: shut('shop program; commerce and concourse functions'),
  [B('leasure_structure')]: shut('shop program; commerce, market, recreation, entertainment and concourse functions; a landmark'),
  [B('output')]: shut('warehouse program; storage, manufacturing and armory functions'),
  [B('mining_facility')]: shut('factory program; industry, manufacturing, utility, repair, fuel and reactor functions'),
  [B('survey_structure')]: shut('science, agriculture and life-support functions — none of them in the allow-set', {
    question: 'civ B\'s survey structure is sealed while civ A\'s opens — decide one answer for both packs',
  }),
  // The ports and the sky-ship hull (placed by the settlement builder beside the plan)
  'structure:spaceport_terminal': shut('the boarding hall — no vocabulary word names it'),
  'structure:landing_pad_structure': equip('a landing pad (the word "pad")'),
  'structure:civa_city_hall': hab('the vocabulary word "hall" — but this model is the SKY-SHIP hull: placeShip diverts it before the registry, so the rule is never asked', {
    recommend: 'sealed',
    question: 'the sky-ship hull would open as a civic hall the day anything else places this key; its decks are SkyShipModule\'s',
  }),
});

// ── The switch (diagnosis only): off = every key takes the legacy vocabulary path, as before this slice ─────────────
let tableOn: boolean | null = null;
/** Is the table consulted? `localStorage atlas:interior-capability = 'off'` (read once) or `setCapabilityTable(false)`. */
export function capabilityTableOn(): boolean {
  if (tableOn === null) {
    try { tableOn = (globalThis as { localStorage?: { getItem(k: string): string | null } }).localStorage?.getItem('atlas:interior-capability') !== 'off'; }
    catch { tableOn = true; }
  }
  return tableOn;
}
export function setCapabilityTable(on: boolean): void { tableOn = on; }

/** A deed type's entry, or null (unknown type, or the table switched off) — then the vocabulary decides. */
export function deedCapability(structureType: string): CapabilityEntry | null {
  return capabilityTableOn() ? (DEED_CAPABILITY[structureType] ?? null) : null;
}
/** A settlement prefab key's entry, or null — then the vocabulary and the caller's allow-set decide. */
export function settlementCapability(key: string): CapabilityEntry | null {
  return capabilityTableOn() ? (SETTLEMENT_CAPABILITY[key] ?? null) : null;
}

// ── The frames two producers register (pure, so the gate tests what the modules call) ───────────────────────────────

/**
 * The enterable frame of a deed: the SEATED footprint centre, the structure row's yaw, the rendered size.
 * THE FRAME NEVER CARRIES THE MODEL YAW: the generated set's (and the guild hall's) entrance is on the model's +Z, so
 * the RENDER and its collider turn by `modelYawOf` (π) to bring it onto the structure frame's −Z — where the shell's
 * door, the door approach the server tests and the placement map's marker already are. An enterable that added π too
 * (the guild hall did until 2026-09-24) puts the shell's door on the wall opposite the entrance.
 */
export function deedEnterablePose(
  structureYaw: number, dp: { footX: number; footZ: number; y: number; w: number; h: number; d: number },
): { x: number; z: number; yaw: number; groundY: number; w: number; h: number; d: number } {
  return { x: dp.footX, z: dp.footZ, yaw: structureYaw, groundY: dp.y, w: dp.w, h: dp.h, d: dp.d };
}

/** What `objectCorners` reads of a scene object — a three `Object3D` fits it; so does a plain record in a harness. */
export interface BoxLike { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } }
export interface CornerSource {
  geometry?: { boundingBox: BoxLike | null; computeBoundingBox(): void };
  /** An object-level box where three keeps one (InstancedMesh, SkinnedMesh, BatchedMesh). */
  boundingBox?: BoxLike | null;
  computeBoundingBox?: () => void;
  matrixWorld: { elements: ArrayLike<number> };
  children: readonly CornerSource[];
}

/**
 * The world-space corners of every bounding box under `root` — exactly the boxes `Box3.setFromObject` unions (the
 * object-level box where there is one, else the geometry's, each under its own world matrix), handed out as POINTS so
 * the extent can be measured in any frame. The caller has updated the world matrices.
 */
export function objectCorners(root: CornerSource): Array<[number, number, number]> {
  const out: Array<[number, number, number]> = [];
  const walk = (o: CornerSource): void => {
    const g = o.geometry;
    if (g) {
      let bb: BoxLike | null;
      if (o.boundingBox !== undefined) { if (o.boundingBox === null) o.computeBoundingBox?.(); bb = o.boundingBox ?? null; }
      else { if (g.boundingBox === null) g.computeBoundingBox(); bb = g.boundingBox; }
      if (bb && bb.max.x >= bb.min.x && bb.max.y >= bb.min.y && bb.max.z >= bb.min.z) {
        const e = o.matrixWorld.elements;
        for (let i = 0; i < 8; i++) {
          const x = i & 1 ? bb.max.x : bb.min.x, y = i & 2 ? bb.max.y : bb.min.y, z = i & 4 ? bb.max.z : bb.min.z;
          out.push([e[0] * x + e[4] * y + e[8] * z + e[12], e[1] * x + e[5] * y + e[9] * z + e[13], e[2] * x + e[6] * y + e[10] * z + e[14]]);
        }
      }
    }
    for (const c of o.children) walk(c);
  };
  walk(root);
  return out;
}

/**
 * The box a marked object is judged and carved by: its extent measured in ITS OWN yaw frame.
 * `points` are world-space corners (each mesh's bounding box under its world matrix); they are turned back by `yaw`
 * about the world Y axis (three's rotation about Y: local (x, z) → (x·c + z·s, −x·s + z·c)), boxed, and the centre is
 * turned forward again. At yaw 0 this IS the world AABB. The marker used to take the world AABB and then turn the
 * shell by the yaw a second time: a 10 × 4 m object at 90° got a 4 × 10 shell lying across it, and at 45° a
 * 9.9 × 9.9 one. Null when there is nothing to measure.
 */
export function orientedBox(
  points: Iterable<readonly [number, number, number]>, yaw: number,
): { x: number; z: number; groundY: number; w: number; h: number; d: number } | null {
  const c = Math.cos(yaw), s = Math.sin(yaw);
  let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity, z0 = Infinity, z1 = -Infinity;
  for (const [wx, wy, wz] of points) {
    const lx = wx * c - wz * s, lz = wx * s + wz * c;
    if (lx < x0) x0 = lx; if (lx > x1) x1 = lx;
    if (wy < y0) y0 = wy; if (wy > y1) y1 = wy;
    if (lz < z0) z0 = lz; if (lz > z1) z1 = lz;
  }
  if (!(x1 >= x0 && y1 >= y0 && z1 >= z0)) return null;
  const cx = (x0 + x1) / 2, cz = (z0 + z1) / 2;
  return { x: cx * c + cz * s, z: -cx * s + cz * c, groundY: y0, w: x1 - x0, h: y1 - y0, d: z1 - z0 };
}
